// This component displays our whole course catalog on the LOHP.
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {border, color, font, spacing} from "@khanacademy/wonder-blocks-tokens";
import {RequestInfo} from "@khan/shared-core";
import {staticUrl} from "@khan/urls";

import type {DomainObject} from "#page-package/types.ts";

import caretDownIcon from "@phosphor-icons/core/bold/caret-down-bold.svg";

export const isTablet: any = "@media screen and (min-width: 768px)";
export const isDesktop: any = "@media screen and (min-width: 1023.5px)";

type Props = {
    domains: Array<DomainObject>;
    expandedByDefault: boolean;
};

type State = {
    courseListVisible: any;
};

class CourseList extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const domainsVisible: Record<string, boolean> = {};
        props.domains.forEach((domain) => {
            if (!RequestInfo.isMobile) {
                // For desktop, set the expaned state to match the default prop
                // value.
                domainsVisible[domain.translatedTitle] =
                    props.expandedByDefault;
            } else {
                // Mobile and tablet are always closed, don't modify this
                domainsVisible[domain.translatedTitle] = false;
            }
        });
        this.state = {
            courseListVisible: domainsVisible,
        };
    }

    splitInTwo(arr: Array<any>): Array<Array<any>> {
        const half = Math.ceil(arr.length / 2);
        return [arr.slice(0, half), arr.slice(half)];
    }

    toggleDomainList(translatedTitle: string) {
        this.setState({
            courseListVisible: {
                ...this.state.courseListVisible,
                [translatedTitle]:
                    !this.state.courseListVisible[translatedTitle],
            },
        });
    }

    render(): React.ReactNode {
        const {domains} = this.props;
        const {courseListVisible} = this.state;
        // we manually split into two columns instead of just using css grid
        // because grid will display across first like:
        // 1 2
        // 3 4
        // but we want top down first like:
        // 1 3
        // 2 4
        const domainCols = this.splitInTwo(domains);
        return (
            <View tag="nav" style={styles.domainList} aria-label={t`Courses`}>
                <View style={styles.domainListInner}>
                    {domainCols.map((domainCol, n) => (
                        <View key={n}>
                            {domainCol.map((domain, i) => (
                                <View
                                    key={i}
                                    style={[
                                        styles.domainGroup,
                                        n === 1 &&
                                        i === domainCols[1].length - 1
                                            ? styles.lastDomainGroup
                                            : null,
                                    ]}
                                >
                                    <Clickable
                                        style={styles.domainButton}
                                        onClick={() =>
                                            this.toggleDomainList(
                                                domain.translatedTitle,
                                            )
                                        }
                                        aria-expanded={
                                            courseListVisible[
                                                domain.translatedTitle
                                            ]
                                        }
                                    >
                                        {() => (
                                            <>
                                                <img
                                                    src={staticUrl(domain.icon)}
                                                    width="45"
                                                    height="45"
                                                    className={css(
                                                        styles.domainIcon,
                                                    )}
                                                    alt=""
                                                />
                                                {domain.translatedTitle}
                                                <PhosphorIcon
                                                    icon={caretDownIcon}
                                                    size="medium"
                                                    color={color.offBlack}
                                                    style={[
                                                        styles.chevron,
                                                        courseListVisible[
                                                            domain
                                                                .translatedTitle
                                                        ]
                                                            ? styles.upChevron
                                                            : null,
                                                    ]}
                                                />
                                            </>
                                        )}
                                    </Clickable>

                                    {/* Splitting the columns the same way we
                                split the domain lists so the tab order goes
                                down first, then to the second column */}
                                    {this.splitInTwo(domain.children).map(
                                        (courseGroup, m) => (
                                            <ul
                                                className={css(
                                                    styles.courseList,
                                                    m === 1
                                                        ? styles.lastCourseList
                                                        : null,
                                                )}
                                                hidden={
                                                    !courseListVisible[
                                                        domain.translatedTitle
                                                    ]
                                                }
                                                key={m}
                                            >
                                                {courseGroup.map(
                                                    (course, j) => (
                                                        <li
                                                            className={css(
                                                                course.href ===
                                                                    domain.href
                                                                    ? styles.seeAllCourse
                                                                    : null,
                                                            )}
                                                            key={j}
                                                        >
                                                            <Link
                                                                href={
                                                                    course.href
                                                                }
                                                                style={
                                                                    styles.courseLink
                                                                }
                                                            >
                                                                <React.Fragment>
                                                                    <span
                                                                        id={`course-title-${n}${i}${m}${j}`}
                                                                    >
                                                                        {
                                                                            course.translatedTitle
                                                                        }
                                                                    </span>
                                                                    {/* This badge is not on brand, add back in when we have guidance from design */}
                                                                    {/* {course.isNew && (
                                                            <React.Fragment>
                                                                <Strut
                                                                    size={
                                                                        Spacing.xxxSmall_4
                                                                    }
                                                                    style={{
                                                                        display:
                                                                            "inline-block",
                                                                    }}
                                                                />
                                                                <NewBadge />
                                                            </React.Fragment>
                                                        )} */}
                                                                </React.Fragment>
                                                            </Link>
                                                        </li>
                                                    ),
                                                )}
                                            </ul>
                                        ),
                                    )}
                                </View>
                            ))}
                        </View>
                    ))}
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    domainList: {
        margin: "20px auto -20px auto",
        maxWidth: 1200,
    },
    domainListInner: {
        padding: spacing.medium_16,
        marginLeft: spacing.medium_16,
        marginRight: spacing.medium_16,
        backgroundColor: "#F5F5F5",
        borderRadius: border.radius.radius_040,
        WebkitFontSmoothing: "antialiased",
        [isDesktop]: {
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            columnGap: "100px",
            padding: "20px 80px",
        },
    },
    domainButton: {
        fontWeight: font.weight.bold,
        fontSize: font.size.medium,
        lineHeight: font.lineHeight.medium,
        paddingBlock: spacing.small_12,
        color: color.offBlack,
        textAlign: "left",
        border: "none",
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        alignItems: "center",
        backgroundColor: "transparent",
        [isDesktop]: {
            paddingBlock: spacing.medium_16,
            gridColumnStart: "1",
            gridColumnEnd: "3",
            fontSize: font.size.large,
        },
    },
    domainIcon: {
        borderRadius: border.radius.radius_full,
        marginRight: spacing.small_12,
        width: "34px",
        height: "34px",
        [isDesktop]: {
            width: "45px",
            height: "45px",
        },
    },
    domainGroup: {
        borderBottom: `${border.width.thin} solid ${color.offBlack32}`,
        [isDesktop]: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            ":last-child": {
                border: 0,
            },
        },
    },
    lastDomainGroup: {
        borderBottom: 0,
    },
    courseList: {
        // this is the width of the icon domainIcon plus the right margin
        marginLeft: 46,
        [isDesktop]: {
            marginTop: -5,
            marginBottom: spacing.small_12,
            marginLeft: 57,
        },
    },
    lastCourseList: {
        marginBottom: spacing.xSmall_8,
        [isDesktop]: {
            marginLeft: spacing.large_24,
        },
    },
    seeAllCourse: {
        fontWeight: font.weight.bold,
    },
    courseLink: {
        fontSize: font.size.medium,
        lineHeight: font.lineHeight.medium,
        paddingBlock: spacing.xSmall_8,
        display: "block",
        color: color.offBlack,
    },
    chevron: {
        transition: "transform .5s",
    },
    upChevron: {
        transform: "rotate(-180deg)",
    },
});

export default CourseList;
